import type { Ref } from 'react'
import { useState, useEffect } from 'react'
import { Spacer } from '@truepill/react-capsule'
import { isRefillTooSoonReason } from '@truepill/tpos-data-util'
import { useMutation } from '@truepill/tpos-react-router'
import {
  MiscellaneousTriageReasons,
  PV1TriageReasons,
  CopayTriageReasons,
  MedicarePaymentPlanCopayTriages,
  TriageReasonsToExcludeFromManualAction,
  LaunchDarkly,
  getTriageReasonsByStatus,
} from '@truepill/tpos-types'
import type { TriageEntry, TriageReasons, OrderStatus } from '@truepill/tpos-types'
import { ReactComponent as DangerIcon } from 'assets/icons/danger.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { SaveButton } from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { ModalHeader, ModalWrapper } from 'components/Modal'
import { ActionButton, CancelButton } from 'components/PageStructure'
import { StyledSelect } from 'components/RXTable'
import StyledDatePicker from 'components/StyledDatePicker'
import TriageAlert from 'components/TriageAlert'
import Warning from 'components/Warning'
import { SET_IN_TRIAGE, SET_COPAY_IN_TRIAGE } from 'gql'
import { TextArea, Box } from 'grommet'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import useAutoFocus from 'hooks/useAutoFocus'
import moment from 'moment'
import { useClient, useFlag } from 'providers/LaunchDarklyProvider'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { update } from 'ramda'
import styled from 'styled-components'
import { bodyPrimaryColor, borderColor, primaryBackgroundColor } from 'styles/styleVariables'
import type { QueueItem, OrderTriage } from 'types'
import { isOrder } from 'utils'
interface FieldsProps {
  index: number
  value: TriageEntry
  item: QueueItem
  fillId?: string
  onChange: (triageEntry: TriageEntry, index: number) => void
}

const defaultTriageReasons = [MiscellaneousTriageReasons.Other]
const hideTriages = [CopayTriageReasons.CoverageTerminated, ...MedicarePaymentPlanCopayTriages]

const TriageEntryFields = (props: FieldsProps): JSX.Element => {
  const { item } = props
  const itemIsOrder = isOrder(item)
  const autoFocusRef = useAutoFocus()

  const [triageReasons, setTriageReasons] = useState<string[]>(defaultTriageReasons)
  const [alertSelectedReason, setAlertSelectedReason] = useState(false)
  const alertEnabledForSelectedReasonFlag = useFlag(LaunchDarkly.FeatureFlags.ALERT_CLINICAL_OUTREACH_TO_MD_TRIAGE)
  const { ldReady, ldIdentify } = useClient()
  const isRefillTooSoon = isRefillTooSoonReason(props.value.selectedReason)
  const itemHasOtc = isOrder(item) && !!item.otcProducts?.length

  useEffect(() => {
    const reasons = itemIsOrder
      ? getTriageReasonsByStatus(item.status, itemHasOtc)?.sort() ?? defaultTriageReasons
      : [...Object.values(CopayTriageReasons).filter(reason => !hideTriages.includes(reason))]

    const filteredReasons = reasons.filter(reason => !TriageReasonsToExcludeFromManualAction.includes(reason))

    setTriageReasons(filteredReasons)
  }, [item.status, itemIsOrder, itemHasOtc])

  useEffect(() => {
    if (item.customer?.legacyId && ldReady) {
      ldIdentify({ key: item.customer.legacyId.toString() })
    }
  }, [ldReady, ldIdentify, item.customer])

  return (
    <InputContainer data-index={props.index}>
      {props.index > 0 && <TriageIndexHeader>Triage reason {props.index + 1}</TriageIndexHeader>}
      <ReasonSelect
        data-testid="reason"
        ref={autoFocusRef as Ref<HTMLButtonElement>}
        multiple={false}
        maximumHeight="12rem"
        modal={true}
        value={props.value.selectedReason}
        placeholder="Select a triage reason..."
        options={triageReasons}
        onChange={([option]) => {
          const selectedReason = option ? option.value : ''
          setAlertSelectedReason(selectedReason === PV1TriageReasons.ClinicalOutreachToMD)
          props.onChange({ ...props.value, reason: selectedReason, selectedReason, fillId: props.fillId }, props.index)
        }}
      />
      {isRefillTooSoon && (
        <StyledDatePicker
          date={props.value.nextFillableDate ? moment(props.value.nextFillableDate, 'MM/DD/YYYY').toDate() : undefined}
          onChange={date => {
            const dateStr = moment(date).format('MM/DD/YYYY')
            const newEntry = {
              ...props.value,
              nextFillableDate: dateStr,
              fillId: props.fillId,
              inTriageUserMessage: `Refill available from ${dateStr}`,
            }
            props.onChange(newEntry, props.index)
          }}
          showPlaceholder={props.value.nextFillableDate === undefined}
        />
      )}
      <Spacer desktop="xl" />
      <Warning text="Any message will display to patients and external customers" />
      {alertEnabledForSelectedReasonFlag && alertSelectedReason && (
        <TriageAlert text="Your inputted message will be visible to our Prescribers. Please include as much info and clarity so that we can resolve this triage as soon as possible." />
      )}
      <TextArea
        data-testid="message"
        onChange={event =>
          props.onChange({ ...props.value, inTriageUserMessage: event.target.value, fillId: props.fillId }, props.index)
        }
        placeholder="Type a message..."
        value={props.value.inTriageUserMessage}
        resize="vertical"
      />
    </InputContainer>
  )
}

const defaultTriageEntry = (): TriageEntry => ({
  reason: '',
})

type TriagePayload = Pick<OrderTriage, 'fillId' | 'reason' | 'inTriageUserMessage' | 'rxChange' | 'details'>
type TriageModalProps = {
  fillId?: string
  item: QueueItem
  confirmationCallback: () => void
  onDismiss?: () => void
}

const TriageModal = ({ fillId, item, confirmationCallback, onDismiss }: TriageModalProps): JSX.Element => {
  const [triages, setTriages] = useState<TriageEntry[]>([defaultTriageEntry()])
  const { dismissModal } = useModalContext()

  const [setInTriageMutation] = useMutation<
    {
      setInTriage: { _id: string; inTriage: { reason: TriageReasons } }
      status: OrderStatus
      triages: TriagePayload[]
    },
    { orderId: string; triages: TriagePayload[] }
  >(SET_IN_TRIAGE, {
    onCompleted: () => {
      showSuccessToast(`Set triage for order ${item._id}`)
      confirmationCallback()
      dismissModal()
    },
    onError: error => {
      console.error('Failed to set triage:', error)
      showErrorToast(`Failed to set triage for order ${item._id}: ${error?.message ?? error}`)
      dismissModal()
    },
  })

  const [setCopayInTriageMutation] = useMutation<{ copayRequestId: string; triages: TriagePayload[] }>(
    SET_COPAY_IN_TRIAGE,
    {
      onCompleted: () => {
        showSuccessToast(`Set triage for copay ${item._id}`)
        confirmationCallback()
        dismissModal()
      },
      onError: error => {
        console.error('Failed to set triage:', error)
        showErrorToast(`Failed to set triage for copay ${item._id}: ${error?.message ?? error}`)
        dismissModal()
      },
    },
  )

  const showErrorToast = useErrorToast()
  const showSuccessToast = useSuccessToast()

  const reasonGiven = triages[0].reason !== ''
  const needsNextFillDate = triages.some(
    entry => isRefillTooSoonReason(entry.selectedReason) && !entry.nextFillableDate,
  )
  const disableSave = !reasonGiven || needsNextFillDate

  const handleTriageUpdate = (updatedTriage: TriageEntry, index: number) => {
    const updatedTriageEntries = update(index, updatedTriage, triages)
    setTriages(updatedTriageEntries)
  }
  const handleAddTriage = () => setTriages([...triages, defaultTriageEntry()])

  return (
    <ModalWrapper id="TriageModal">
      <ModalHeader closeCallback={onDismiss}>
        <IconWrapper>
          <DangerIcon fill={bodyPrimaryColor} />
        </IconWrapper>
        <h2>Triage reason</h2>
      </ModalHeader>
      {triages.map((triageEntry, index) => (
        <TriageEntryFields
          item={item}
          key={index}
          index={index}
          value={triageEntry}
          fillId={fillId}
          onChange={handleTriageUpdate}
        />
      ))}
      <Box margin={{ top: '1rem' }} width={'15rem'}>
        <StyledActionButton
          data-testid="add-reason"
          icon={
            <IconWrapper>
              <PlusIcon fill="black" />
            </IconWrapper>
          }
          label="Add another triage reason"
          onClick={handleAddTriage}
        />
      </Box>
      <ButtonsContainer>
        <CancelButton
          label="Cancel"
          onClick={() => {
            dismissModal()
            onDismiss && onDismiss()
          }}
        />
        <SaveButton
          isModal
          disabled={disableSave}
          label="Submit"
          onClick={async () => {
            const isOrderItem = isOrder(item)
            const filteredTriages: TriagePayload[] = triages.map(
              ({ reason, fillId, inTriageUserMessage, rxChange, nextFillableDate }) => {
                // Compose the reason and details into the enum keys by removing
                // the spaces added in for display readability
                if (rxChange?.reason) rxChange.reason = rxChange.reason.split(' ').join('')
                if (rxChange?.details) rxChange.details = rxChange.details.map(detail => detail.split(' ').join(''))
                // at this point, because this can only be clicked if reasonGiven is true, then we can safely assert
                // reason is not an empty string and thus is a TriageReason
                const payload: TriagePayload = {
                  reason: reason as TriageReasons,
                  fillId,
                  inTriageUserMessage,
                  rxChange,
                }
                if (nextFillableDate) {
                  payload.details = {
                    dateOkToFill: moment(nextFillableDate, 'MM/DD/YYYY').toDate(),
                  }
                }
                return payload
              },
            )

            if (isOrderItem) {
              await setInTriageMutation({ variables: { orderId: item._id, triages: filteredTriages } })
            } else {
              await setCopayInTriageMutation({ variables: { copayRequestId: item._id, triages: filteredTriages } })
            }
          }}
        />
      </ButtonsContainer>
    </ModalWrapper>
  )
}

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-top: 1.25rem;
`

const TriageIndexHeader = styled.h3`
  margin: 1.25rem 0 1.125rem;
`

const InputContainer = styled.div`
  width: 40rem;
  margin-top: 0.625rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  > textarea {
    padding-top: 0.3125rem;
    height: 7rem;
    margin-top: 0.625rem;
    border: 0.125rem solid ${borderColor};
  }
`

const StyledActionButton = styled(ActionButton)`
  margin-left: 0rem;
  background-color: ${primaryBackgroundColor};
`

const ReasonSelect = styled(StyledSelect)`
  height: 2.5rem;
`

export default TriageModal
